import { Box, Button, Link, Stack, Typography } from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { CopyMarkdownButton } from '../../shared/components/DocumentSections';
import { GlassPanel } from '../../shared/components/GlassPanel';
import { MarkdownContent } from '../../shared/components/MarkdownContent';
import { GenerationProgressPanel } from '../projects/dashboard/GenerationProgressPanel';
import type { Job, Project } from '../../core/types/data-models';
import { hasSourceBrief, type ValidationCopy } from './validationCopy';

/** The links the project was created with, in the order the onboarding wizards ask for them. */
function projectLinks(project: Project): { label: string; url: string }[] {
  return [
    ...project.websiteUrls.map((url, i) => ({ label: i === 0 ? 'Website' : 'Additional website', url })),
    { label: 'GitHub', url: project.githubUrl },
    { label: 'Documentation', url: project.docsUrl },
    { label: 'LinkedIn', url: project.linkedinUrl },
  ].filter((link): link is { label: string; url: string } => Boolean(link.url));
}

/** The first step as one Markdown document: the project name, the story and its links. */
function introMarkdown(project: Project, story: string, links: { label: string; url: string }[]): string {
  const lines = [`# ${project.name}`, '', story];
  if (links.length > 0) lines.push('', '## Links', '', ...links.map((link) => `- ${link.label}: ${link.url}`));
  return lines.join('\n');
}

interface IntroPanelProps {
  project: Project;
  copy: ValidationCopy['intro'];
  /** The running ANALYZE_PROJECT_SOURCES job, if any — shown in place of the brief. */
  analyzeJob: Job | null;
  /** Whether the reader may (re)run the source analysis: a business project, and edit rights. */
  canAnalyze: boolean;
  busy: boolean;
  onAnalyze: () => void;
}

/**
 * The first step, read back: what the project was created from — the idea
 * the founder described, or the company brief written from the sources an
 * investor gave (its `userPrompt`, Markdown) — and the links given with it.
 * A project created before the wizard has no story of its own, only the
 * prompt of its first generation, so that is the fallback. For a business
 * project the analysis can be run again from here.
 */
export function IntroPanel({ project, copy, analyzeJob, canAnalyze, busy, onAnalyze }: IntroPanelProps) {
  const story = (project.userPrompt ?? project.generateUserPrompt ?? '').trim();
  const links = projectLinks(project);
  const briefExists = hasSourceBrief(story);
  const analyzeLabel = briefExists ? copy.analyzeAgainAction : copy.analyzeFirstAction;

  return (
    <GlassPanel sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700}>
            {copy.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {copy.subtitle}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          {canAnalyze && analyzeLabel && !analyzeJob && (
            <Button
              size="small"
              variant={briefExists ? 'outlined' : 'contained'}
              startIcon={briefExists ? <AutorenewIcon /> : <AutoAwesomeIcon />}
              disabled={busy}
              onClick={onAnalyze}
            >
              {analyzeLabel}
            </Button>
          )}
          {story && <CopyMarkdownButton markdown={introMarkdown(project, story, links)} />}
        </Stack>
      </Stack>

      {analyzeJob ? (
        <Box sx={{ mt: 2 }}>
          <GenerationProgressPanel job={analyzeJob} title={copy.analyzing} />
        </Box>
      ) : (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 'var(--radius)',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          {canAnalyze && !briefExists && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: story ? 2 : 0 }}>
              {copy.empty}
            </Typography>
          )}
          {story ? (
            <MarkdownContent>{story}</MarkdownContent>
          ) : (
            !canAnalyze && (
              <Typography variant="body2" color="text.disabled">
                {copy.empty}
              </Typography>
            )
          )}
        </Box>
      )}
      {canAnalyze && briefExists && !analyzeJob && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {copy.analyzeHint}
        </Typography>
      )}

      {links.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Links
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {links.map((link) => (
              <Stack key={`${link.label}-${link.url}`} direction="row" spacing={1} alignItems="baseline">
                <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140, flexShrink: 0 }}>
                  {link.label}
                </Typography>
                <Link
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ wordBreak: 'break-all' }}
                >
                  {link.url}
                </Link>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </GlassPanel>
  );
}
